import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useOutlet, UNSAFE_LocationContext } from 'react-router-dom';

// App View only. The pages behind the bottom tabs stay mounted (hidden) for a short while, so tapping
// back to a tab shows the last screen at once instead of a blank page + spinner, then it is rebuilt
// (fresh data) once it has been hidden longer than MAX_AGE. Hidden pages see a frozen location, so
// URL-driven filters on them never react to other pages. Every other route renders as before.
const MAX_AGE = 30000;

// False inside a kept-but-hidden page. Anything that portals out of the page (floating buttons) must
// read this, because display:none on the page does not hide a portal.
export const PageVisibleContext = React.createContext(true);

export default function KeepAliveOutlet({ tabPaths, nonce = 0 }) {
  const location = useLocation();
  const outlet = useOutlet();
  const liveCtx = useContext(UNSAFE_LocationContext);
  const entries = useRef(new Map()); // key -> { element, ctx, hiddenAt, v, scroll }
  const seq = useRef(0);             // every rebuilt page gets a new, never-reused version
  const lastNonce = useRef(nonce);
  const [, tick] = useState(0);
  const mainEl = useRef(null);
  const rootRef = useRef(null);

  const key = location.pathname + location.search;
  const cacheable = tabPaths.includes(location.pathname);
  const now = Date.now();
  const map = entries.current;

  // Pull-to-refresh: drop the current page so it is rebuilt (and re-fetches its data).
  if (nonce !== lastNonce.current) { lastNonce.current = nonce; map.delete(key); }

  if (cacheable) {
    const e = map.get(key);
    if (!e || (e.hiddenAt && now - e.hiddenAt > MAX_AGE)) {
      map.set(key, { element: outlet, ctx: liveCtx, hiddenAt: null, v: ++seq.current, scroll: 0 });
    } else {
      e.element = outlet; e.ctx = liveCtx; e.hiddenAt = null;
    }
  }
  for (const [k, e] of map) {
    if (cacheable && k === key) continue;
    if (!e.hiddenAt) e.hiddenAt = now;
    else if (now - e.hiddenAt > MAX_AGE) map.delete(k);
  }
  while (map.size > 6) map.delete(map.keys().next().value); // oldest out

  // Unmount expired hidden pages (stops their timers) even if nothing else re-renders.
  const hiddenCount = [...map.values()].filter(e => e.hiddenAt).length;
  useEffect(() => {
    if (!hiddenCount) return undefined;
    const t = setInterval(() => tick(n => n + 1), 10000);
    return () => clearInterval(t);
  }, [hiddenCount]);

  // Remember / restore the scroll position of each kept page.
  useEffect(() => {
    mainEl.current = rootRef.current?.closest('main') || null;
    const m = mainEl.current;
    if (!m) return undefined;
    const onScroll = () => { const e = entries.current.get(key); if (e && cacheable) e.scroll = m.scrollTop; };
    m.addEventListener('scroll', onScroll, { passive: true });
    return () => m.removeEventListener('scroll', onScroll);
  }, [key, cacheable]);
  useLayoutEffect(() => {
    const e = entries.current.get(key);
    if (cacheable && e && mainEl.current) mainEl.current.scrollTop = e.scroll || 0;
  }, [key, cacheable]);

  return (
    <div ref={rootRef}>
      {[...map].map(([k, e]) => {
        const visible = cacheable && k === key;
        return (
          <div key={`${k}#${e.v}`} className="av-page" style={visible ? undefined : { display: 'none' }} aria-hidden={!visible}>
            <PageVisibleContext.Provider value={visible}>
              <UNSAFE_LocationContext.Provider value={visible ? liveCtx : e.ctx}>{e.element}</UNSAFE_LocationContext.Provider>
            </PageVisibleContext.Provider>
          </div>
        );
      })}
      {!cacheable && <div key={`${location.pathname}#${nonce}`} className="av-page">{outlet}</div>}
    </div>
  );
}
